import React from 'react';
import { CheckCircle2Icon, MinusCircleIcon } from 'lucide-react';
import { Liability } from '../types/credit';
import { formatCurrency } from '../data/reports';
import { StatusBadge } from './StatusBadge';
type LiabilityTableProps = {
  liabilities: Liability[];
  pendingCount: number;
  excludedCount: number;
  totalBalance: number;
  totalMonthly: number;
};
export function LiabilityTable({
  liabilities,
  pendingCount,
  excludedCount,
  totalBalance,
  totalMonthly
}: LiabilityTableProps) {
  return <div className="overflow-hidden rounded-lg border border-ink-100 bg-white shadow-sm" data-id="element-33" data-mp="219jum7zyayfz">
      <div className="flex flex-col gap-1 border-b border-ink-100 px-4 py-3 sm:flex-row sm:items-center sm:justify-between" data-id="element-34" data-mp="25bf4i0vy6k5d">
        <p className="text-sm text-ink-500" data-id="element-35" data-mp="2abt0ulo6s5nk">
          <span className="font-medium text-ink-900" data-id="element-36" data-mp="3gr3smvkxtp7r">
            {liabilities.length} liabilities
          </span>{' '}
          on this report
        </p>
        <p className="text-sm text-ink-500" data-id="element-37" data-mp="3lovmhv557lel">
          {pendingCount > 0 ? `All ${pendingCount} new ${pendingCount === 1 ? 'account' : 'accounts'} will be pushed together` : 'Nothing left to push on this report'}
          {excludedCount > 0 && <>
              {' · '}
              <span className="text-ink-500" data-id="element-38" data-mp="3n6c5x6tye0yh">
                {excludedCount} excluded ($0 balance)
              </span>
            </>}
        </p>
      </div>

      <div className="overflow-x-auto" data-id="element-39" data-mp="3e8jzks67m3n8">
        <table className="w-full min-w-[720px] text-left text-sm" data-id="element-40" data-mp="3e9ldr5iiv696">
          <caption className="sr-only" data-id="element-41" data-mp="39g0cn4lmtw5r">
            Liabilities from the selected credit report
          </caption>
          <thead className="bg-ink-50 text-xs uppercase tracking-wide text-ink-500" data-id="element-42" data-mp="34hyt6ikanutr">
            <tr data-id="element-43" data-mp="2zjxdm7m2udn1">
              <th scope="col" className="px-4 py-3 font-semibold" data-id="element-44" data-mp="2zkfejhjopg1a">
                Creditor
              </th>
              <th scope="col" className="w-32 px-4 py-3 font-semibold" data-id="element-45" data-mp="2ulm3dvrve14a">
                Type
              </th>
              <th scope="col" className="w-32 px-4 py-3 font-semibold" data-id="element-46" data-mp="2fwsoaeqcx0hs">
                Owner
              </th>
              <th scope="col" className="w-32 px-4 py-3 font-semibold" data-id="element-47" data-mp="2kvnkcidc4lju">
                Status
              </th>
              <th scope="col" className="w-32 px-4 py-3 text-right font-semibold" data-id="element-48" data-mp="26ww6mx2dx1w8">
                Balance
              </th>
              <th scope="col" className="w-28 px-4 py-3 text-right font-semibold" data-id="element-49" data-mp="2zkrztrsqwbo9">
                Monthly
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-100" data-id="element-50" data-mp="2pv8gkl14efsv">
            {liabilities.map(liability => {
            const excluded = !liability.alreadyPushed && liability.balance <= 0;
            return <tr key={liability.id} className={liability.alreadyPushed || excluded ? 'bg-ink-50/70' : undefined} data-id="element-51" data-mp="3ij4bplb1jznj">
                <td className="px-4 py-3.5 align-middle" data-id="element-52" data-mp="i8s7lo4hte6">
                  <div className={`font-medium leading-tight ${excluded ? 'text-ink-500' : 'text-ink-900'}`} data-id="element-53" data-mp="3so49hve6855d">
                    {liability.creditor}
                  </div>
                  <div className="mt-0.5 flex items-center gap-2 text-xs leading-tight text-ink-500" data-id="element-54" data-mp="5a8nh0t8zy4f">
                    {liability.accountNumber}
                    {liability.alreadyPushed && <span className="inline-flex items-center gap-1 font-medium text-green-700" data-id="element-55" data-mp="39pi444bmfrvn">
                        <CheckCircle2Icon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-56" data-mp="3norkp53qoo05" />
                        In Encompass
                      </span>}
                    {excluded && <span className="inline-flex items-center gap-1 font-medium text-ink-500" data-id="element-57" data-mp="xzpcdohnswvw">
                        <MinusCircleIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-58" data-mp="33nzqio3tpn5l" />
                        Not pushed · $0 balance
                      </span>}
                  </div>
                </td>
                <td className="px-4 py-3.5 align-middle text-ink-700" data-id="element-59" data-mp="3hnr3qmmimzij">
                  {liability.accountType}
                </td>
                <td className="px-4 py-3.5 align-middle text-ink-700" data-id="element-60" data-mp="3mlsuzuu3i553">
                  {liability.owner}
                </td>
                <td className="px-4 py-3.5 align-middle" data-id="element-61" data-mp="3rj2g4yagbun4">
                  <StatusBadge status={liability.status} data-id="element-62" data-mp="82gcf5nwf79" />
                </td>
                <td className="px-4 py-3.5 text-right align-middle tabular-nums text-ink-900" data-id="element-63" data-mp="563x2tfm82fk">
                  {formatCurrency(liability.balance)}
                </td>
                <td className="px-4 py-3.5 text-right align-middle tabular-nums text-ink-900" data-id="element-64" data-mp="e6ili1nvlxna">
                  {formatCurrency(liability.monthlyPayment)}
                </td>
              </tr>;
          })}
          </tbody>
          <tfoot className="border-t border-ink-100 bg-ink-50 text-sm" data-id="element-65" data-mp="1tof3mw66te0g">
            <tr data-id="element-66" data-mp="1pnd4efc9t1yh">
              <td colSpan={4} className="px-4 py-3 font-medium text-ink-700" data-id="element-67" data-mp="1pn3gcxasd4vf">
                Report totals
              </td>
              <td className="px-4 py-3 text-right font-semibold tabular-nums text-ink-900" data-id="element-68" data-mp="2hesz6wnzduzs">
                {formatCurrency(totalBalance)}
              </td>
              <td className="px-4 py-3 text-right font-semibold tabular-nums text-ink-900" data-id="element-69" data-mp="2de937wf58xf8">
                {formatCurrency(totalMonthly)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>;
}